/*
 * Sections du formulaire d'administration, extraites de la page pour
 * lisibilite et maintenance. Chaque section recoit son etat groupe et une
 * fonction de mise a jour partielle (patch).
 */

import { Settings2, Image, FileText, ListChecks, Calculator, ClipboardCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ACTION_CATEGORIES, DIMENSION_LABELS } from "@/hooks/useAdminSettings";

const sectionClass = "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-5";
const inputClass = "mt-1.5 min-h-[48px] rounded-xl border-[rgba(35,82,119,0.18)]";

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-4 h-4 text-[#235277]" aria-hidden="true" />
    <h2 className="font-heading text-base font-semibold text-[#060606]">{children}</h2>
  </div>
);

/* ----- Marque ----- */

export const BrandSection = ({ brand, onPatch }) => (
  <section className={sectionClass}>
    <SectionTitle icon={Image}>Marque</SectionTitle>
    <div>
      <Label htmlFor="program-name" className="text-sm font-medium text-[#060606]">Nom du programme</Label>
      <Input
        id="program-name"
        data-testid="admin-program-name-input"
        value={brand.programName}
        onChange={(e) => onPatch({ programName: e.target.value })}
        className={inputClass}
      />
    </div>
    <div>
      <Label htmlFor="logo-url" className="text-sm font-medium text-[#060606]">URL du logo officiel</Label>
      <Input
        id="logo-url"
        data-testid="admin-logo-url-input"
        value={brand.logoUrl}
        onChange={(e) => onPatch({ logoUrl: e.target.value })}
        className={inputClass}
        placeholder="https://…"
      />
    </div>
  </section>
);

/* ----- Conversion et cohorte ----- */

export const ConversionSection = ({ conversion, onPatch }) => (
  <section className={sectionClass}>
    <SectionTitle icon={Settings2}>Conversion et cohorte</SectionTitle>
    <div>
      <Label htmlFor="booking-url" className="text-sm font-medium text-[#060606]">URL du calendrier de réservation</Label>
      <Input
        id="booking-url"
        data-testid="admin-booking-url-input"
        value={conversion.bookingUrl}
        onChange={(e) => onPatch({ bookingUrl: e.target.value })}
        className={inputClass}
        placeholder="https://…"
      />
      <p className="mt-1.5 text-xs text-[#9E9E9E]">
        L’identifiant de diagnostic est ajouté automatiquement à cette URL au moment du clic,
        sous la forme d’un paramètre nommé diagnostic. Aucune autre donnée n’est transmise.
      </p>
    </div>
    <div>
      <Label htmlFor="price" className="text-sm font-medium text-[#060606]">Tarif de référence</Label>
      <Input
        id="price"
        data-testid="admin-displayed-price-input"
        value={conversion.priceReference}
        onChange={(e) => onPatch({ priceReference: e.target.value })}
        className={inputClass}
      />
      <p className="mt-1.5 text-xs text-[#9E9E9E]">
        Usage interne uniquement. Ce tarif n’est jamais affiché aux prospectes :
        l’investissement est présenté avec transparence pendant l’entretien.
      </p>
    </div>
    <div>
      <Label htmlFor="capacity" className="text-sm font-medium text-[#060606]">Capacité de la cohorte pilote</Label>
      <Input
        id="capacity"
        type="number"
        min="1"
        data-testid="admin-cohort-capacity-input"
        value={conversion.capacity}
        onChange={(e) => onPatch({ capacity: e.target.value })}
        className={inputClass}
      />
    </div>
  </section>
);

/* ----- Bareme de scoring ----- */

export const ScoringSection = ({ scoring, onPatch }) => (
  <section className={sectionClass} data-testid="admin-scoring-section">
    <SectionTitle icon={Calculator}>Barème de scoring</SectionTitle>
    <p className="text-xs text-[#9E9E9E]">
      Pondérations, seuils, questions inversées et niveaux de restitution sont centralisés ici.
      Le calcul reste reproductible : mêmes réponses et même configuration donnent le même bilan.
      La version du barème est affichée aux utilisatrices dans l’explication du score.
    </p>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="bareme-version" className="text-sm font-medium text-[#060606]">Version du barème</Label>
        <Input
          id="bareme-version"
          data-testid="admin-bareme-version-input"
          value={scoring.baremeVersion}
          onChange={(e) => onPatch({ baremeVersion: e.target.value })}
          className={inputClass}
        />
      </div>
      <div>
        <Label htmlFor="ready-threshold" className="text-sm font-medium text-[#060606]">Seuil profil prête (0 à 100)</Label>
        <Input
          id="ready-threshold"
          type="number"
          min="0"
          max="100"
          data-testid="admin-ready-threshold-input"
          value={scoring.readyThreshold}
          onChange={(e) => onPatch({ readyThreshold: e.target.value })}
          className={inputClass}
        />
      </div>
    </div>
    <div>
      <p className="text-sm font-medium text-[#060606] mb-2">Pondérations des dimensions</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {DIMENSION_LABELS.map(({ key, label }) => (
          <div key={key}>
            <Label htmlFor={`weight-${key}`} className="text-xs text-[#333333]">{label}</Label>
            <Input
              id={`weight-${key}`}
              type="number"
              step="0.01"
              min="0"
              data-testid={`admin-weight-${key}-input`}
              value={scoring.weights[key] ?? ""}
              onChange={(e) => onPatch({ weights: { ...scoring.weights, [key]: e.target.value } })}
              className="mt-1 min-h-[42px] rounded-xl border-[rgba(35,82,119,0.18)]"
            />
          </div>
        ))}
      </div>
    </div>
    <div>
      <Label htmlFor="inverted-questions" className="text-sm font-medium text-[#060606]">Questions inversées (séparées par des virgules)</Label>
      <Input
        id="inverted-questions"
        data-testid="admin-inverted-questions-input"
        value={scoring.invertedQuestions}
        onChange={(e) => onPatch({ invertedQuestions: e.target.value })}
        className={inputClass}
        placeholder="q11, q17, q19, q21"
      />
    </div>
    <div>
      <Label htmlFor="restitution-levels" className="text-sm font-medium text-[#060606]">Niveaux de restitution (JSON)</Label>
      <Textarea
        id="restitution-levels"
        data-testid="admin-restitution-levels-input"
        value={scoring.restitutionLevels}
        onChange={(e) => onPatch({ restitutionLevels: e.target.value })}
        rows={6}
        className="mt-1.5 rounded-xl border-[rgba(35,82,119,0.18)] text-xs font-mono leading-relaxed"
      />
    </div>
  </section>
);

/* ----- Textes de consentement ----- */

const CONSENT_FIELDS = [
  { key: "sensible", label: "Consentement sensible (traitement local, obligatoire avant le diagnostic)" },
  { key: "service", label: "Consentement service (envoi du bilan)" },
  { key: "marketing", label: "Consentement marketing (facultatif)" },
  { key: "phone", label: "Consentement téléphone (facultatif)" },
];

export const ConsentSection = ({ consents, onPatch }) => (
  <section className={sectionClass} data-testid="admin-consents-section">
    <SectionTitle icon={ClipboardCheck}>Textes de consentement</SectionTitle>
    <p className="text-xs text-[#9E9E9E]">
      Le consentement de service (envoi du bilan) est distinct du consentement marketing,
      facultatif et jamais pré coché. Chaque acceptation est enregistrée avec sa date et la
      version indiquée ci dessous.
    </p>
    <div>
      <Label htmlFor="consent-version" className="text-sm font-medium text-[#060606]">Version des textes</Label>
      <Input
        id="consent-version"
        data-testid="admin-consent-version-input"
        value={consents.version}
        onChange={(e) => onPatch({ version: e.target.value })}
        className={inputClass}
      />
    </div>
    {CONSENT_FIELDS.map(({ key, label }) => (
      <div key={key}>
        <Label htmlFor={`consent-${key}`} className="text-sm font-medium text-[#060606]">{label}</Label>
        <Textarea
          id={`consent-${key}`}
          data-testid={`admin-consent-${key}-input`}
          value={consents[key]}
          onChange={(e) => onPatch({ [key]: e.target.value })}
          rows={3}
          className="mt-1.5 rounded-xl border-[rgba(35,82,119,0.18)] text-sm leading-relaxed"
        />
      </div>
    ))}
  </section>
);

/* ----- Politique de confidentialite structuree ----- */

export const PrivacySectionsEditor = ({ privacySections, onUpdateSection }) => (
  <section className={sectionClass} data-testid="admin-privacy-section">
    <SectionTitle icon={FileText}>Politique de confidentialité</SectionTitle>
    <p className="text-xs text-[#5E5515]">
      Chaque section marquée comme nécessitant une validation affiche un signal visible sur la
      page publique. Décoche la case une fois l’information définitive validée par un professionnel.
    </p>
    {privacySections.map((section, index) => (
      <div key={section.key || index} className="rounded-xl border border-[rgba(35,82,119,0.14)] p-4 space-y-3">
        <div>
          <Label htmlFor={`privacy-title-${index}`} className="text-xs text-[#333333]">Titre</Label>
          <Input
            id={`privacy-title-${index}`}
            data-testid={`admin-privacy-title-${section.key}`}
            value={section.title || ""}
            onChange={(e) => onUpdateSection(index, "title", e.target.value)}
            className="mt-1 min-h-[42px] rounded-xl border-[rgba(35,82,119,0.18)]"
          />
        </div>
        <div>
          <Label htmlFor={`privacy-content-${index}`} className="text-xs text-[#333333]">Contenu</Label>
          <Textarea
            id={`privacy-content-${index}`}
            data-testid={`admin-privacy-content-${section.key}`}
            value={section.content || ""}
            onChange={(e) => onUpdateSection(index, "content", e.target.value)}
            rows={4}
            className="mt-1 rounded-xl border-[rgba(35,82,119,0.18)] text-sm leading-relaxed"
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id={`privacy-validation-${index}`}
            data-testid={`admin-privacy-validation-${section.key}`}
            checked={Boolean(section.needs_validation)}
            onCheckedChange={(v) => onUpdateSection(index, "needs_validation", Boolean(v))}
          />
          <label htmlFor={`privacy-validation-${index}`} className="text-xs text-[#333333] cursor-pointer">
            Validation professionnelle requise avant le lancement public
          </label>
        </div>
      </div>
    ))}
  </section>
);

/* ----- Bibliotheque d'actions ----- */

export const ActionsLibrarySection = ({ actionsText, onPatchAction }) => (
  <section className={sectionClass}>
    <SectionTitle icon={ListChecks}>Bibliothèque d’actions</SectionTitle>
    <p className="text-xs text-[#9E9E9E]">
      Une action par ligne. La catégorie prudence santé remplace intégralement les actions
      sportives standards lorsque la branche de sécurité est activée.
    </p>
    {ACTION_CATEGORIES.map(({ key, label }) => (
      <div key={key}>
        <Label htmlFor={`actions-${key}`} className="text-sm font-medium text-[#060606]">{label}</Label>
        <Textarea
          id={`actions-${key}`}
          data-testid={`admin-actions-${key}-input`}
          value={actionsText[key] || ""}
          onChange={(e) => onPatchAction(key, e.target.value)}
          rows={4}
          className="mt-1.5 rounded-xl border-[rgba(35,82,119,0.18)] text-sm leading-relaxed"
        />
      </div>
    ))}
  </section>
);
